import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Form, Input, InputNumber, Select, Spin, Tag, Steps, Switch, Tooltip } from 'antd';
import { 
    AppstoreOutlined, 
    CheckCircleFilled, 
    LeftOutlined, 
    RightOutlined, 
    ReloadOutlined,
    UserOutlined,
    EditOutlined,
    BugOutlined,
    ProjectOutlined,
    StopOutlined,
    TeamOutlined,
    UnorderedListOutlined,
    SwapOutlined,
    StarOutlined,
    LockOutlined,
    SettingOutlined
} from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import { SliderTab } from '@/components/ui/SliderTab';
import { Container } from '@/components/ui/Container';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    SliderTabStripScroll,
    RolesSectionSubtitle,
} from '@/pages/inside/tenant/style';
import { useCreateProjectModal, getRequiredModulesForTemplate } from '@/hooks/projects/useCreateProjectModal';
import { colors, rgbaFromHex } from '@/utils/colorSystem';

const StyledSteps = styled(Steps)`
    padding: 0 20px 24px;
    
    .ant-steps-item-title {
        font-size: 13px !important;
        font-weight: 600 !important;
        color: ${colors.neutral[500]} !important;
    }

    .ant-steps-item-icon {
        background: transparent !important;
        border: none !important;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px !important;
        height: 32px !important;
        margin-right: 8px !important;
        
        .ant-steps-icon {
            font-size: 20px !important;
            color: ${colors.neutral[200]} !important;
        }
    }

    /* Active / Process */
    .ant-steps-item-process {
        .ant-steps-item-title { color: ${colors.neutral[900]} !important; }
        .ant-steps-item-icon .ant-steps-icon { color: ${colors.primary[600]} !important; }
    }

    /* Finished */
    .ant-steps-item-finish {
        .ant-steps-item-title { color: ${colors.neutral[900]} !important; }
        .ant-steps-item-icon .ant-steps-icon { color: ${colors.primary[600]} !important; }
        .ant-steps-item-tail::after { background-color: ${colors.primary[600]} !important; }
    }

    @media (max-width: 576px) {
        padding: 0 10px 12px;
        
        .ant-steps-item {
            padding-inline-start: 0 !important;
            flex: 1 !important;
        }
        
        .ant-steps-item-title {
            display: none !important;
        }
        
        .ant-steps-item-icon {
            margin-right: 0 !important;
            margin-inline-start: auto !important;
            margin-inline-end: auto !important;
        }
        
        .ant-steps-item-container {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
        }
    }
`;

const TemplateList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px;
    max-height: 55vh;
    overflow-y: auto;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: ${colors.neutral[200]}; border-radius: 10px; }
`;

const TemplateCard = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${props => props.$selected ? colors.primary[600] : colors.neutral[200]};
    background: ${props => props.$selected ? colors.primary[50] : colors.neutral[0]};
    border-radius: 16px;
    padding: 16px 20px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    position: relative;
    box-shadow: ${props => props.$selected ? `0 0 0 1px ${colors.primary[600]}` : 'none'};

    &:hover {
        border-color: ${colors.primary[400]};
        background: ${props => props.$selected ? colors.primary[50] : colors.neutral[50]};
    }

    .card-header-row {
        display: flex;
        align-items: center;
        gap: 20px;
        width: 100%;

        @media (max-width: 576px) {
            flex-wrap: wrap;
            gap: 12px;
        }
    }

    .illustration-box {
        width: 80px; height: 64px; background: #fff; border-radius: 12px; display: flex; 
        align-items: center; justify-content: center; flex-shrink: 0; border: 1px solid ${colors.neutral[100]};
        overflow: hidden; svg { width: 100%; height: 100%; }

        @media (max-width: 576px) {
            width: 52px;
            height: 40px;
            border-radius: 8px;
        }
    }

    .content-box {
        flex: 1;
        min-width: 150px;
        .name { font-weight: 700; font-size: 16px; color: ${colors.neutral[900]}; margin-bottom: 2px; }
        .description { font-size: 13px; color: ${colors.neutral[500]}; line-height: 1.4; }

        @media (max-width: 576px) {
            .name { font-size: 14px; }
            .description { font-size: 12px; }
        }
    }

    .meta-box { 
        display: flex; flex-direction: column; align-items: flex-end; gap: 8px; 

        @media (max-width: 576px) {
            width: 100%;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            border-top: 1px dashed ${colors.neutral[200]};
            padding-top: 8px;
            margin-top: 4px;
        }
    }

    .check-icon { 
        color: ${colors.primary[600]}; 
        font-size: 20px; 
        visibility: ${props => props.$selected ? 'visible' : 'hidden'}; 

        @media (max-width: 576px) {
            margin-left: auto;
        }
    }

    @media (max-width: 576px) {
        padding: 12px 14px;
        border-radius: 12px;
    }
`;



const ModuleTogglesPanel = styled.div`
    margin-top: 16px;
    border-top: 1px dashed ${rgbaFromHex(colors.primary[600], 0.2)};
    padding-top: 16px;
    width: 100%;
    display: block;
`;

const ModuleTogglesPanelHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    
    .panel-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        background: ${rgbaFromHex(colors.primary[600], 0.1)};
        color: ${colors.primary[600]};
        font-size: 12px;
    }
    
    .panel-title {
        font-size: 12px;
        font-weight: 700;
        color: ${colors.neutral[700]};
        letter-spacing: 0.3px;
    }
    
    .panel-subtitle {
        font-size: 10px;
        color: ${colors.neutral[400]};
        margin-left: auto;
        font-weight: 500;
    }
`;

const ModuleTogglesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 8px;
    width: 100%;

    @media (max-width: 576px) {
        grid-template-columns: 1fr;
        gap: 8px;
    }
`;

const ModuleToggleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${rgbaFromHex(colors.neutral[100], 0.35)};
    border: none;
    transition: all 0.15s ease;
    
    &:hover {
        background: ${rgbaFromHex(colors.primary[50], 0.8)};
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
    }
    
    .toggle-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        flex-shrink: 0;
        font-size: 13px;
        transition: all 0.2s ease;
    }
    
    .toggle-content {
        flex: 1;
        min-width: 0;
        
        .toggle-label {
            font-size: 12px;
            font-weight: 600;
            color: ${colors.neutral[800]};
            display: flex;
            align-items: center;
            gap: 4px;
        }
        
        .toggle-desc {
            font-size: 10px;
            color: ${colors.neutral[400]};
            line-height: 1.3;
            margin-top: 1px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            @media (max-width: 576px) {
                white-space: normal;
                text-overflow: clip;
            }
        }
    }
    
    .toggle-switch {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 6px;
    }
    
    ${props => props.$disabled && css`
        opacity: 0.8;
        background: ${colors.neutral[50]};
        .toggle-label { color: ${colors.neutral[500]}; }
    `}

    @media (max-width: 576px) {
        padding: 8px 10px;
    }
`;

const FormRow = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;
    
    @media (max-width: 576px) {
        flex-direction: column;
        gap: 0;
        
        .ant-form-item {
            margin-bottom: 12px !important;
        }
    }
    
    & > * {
        flex: 1;
        min-width: 0;
    }
`;

const ResponsiveFooter = styled(CreateRoleModalFooter)`
    align-items: center;
    justify-content: space-between;
    width: 100%;
    display: flex;

    @media (max-width: 576px) {
        padding-top: 12px;
        gap: 8px;
        
        button {
            height: 32px;
            padding: 0 12px;
            font-size: 12px;
            border-radius: 8px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        
        & > div {
            gap: 8px !important;
        }
    }
`;

const MODULE_SWITCH_CONFIG = [
    {
        key: 'isQA',
        label: 'QA Testing',
        description: 'Quality assurance submissions and bug tracking',
        icon: <BugOutlined />,
        color: colors.error?.[500] || '#ef4444',
        bgColor: colors.error?.[50] || '#fef2f2',
    },
    {
        key: 'isPMO',
        label: 'PMO',
        description: 'Project management office oversight and reviews',
        icon: <ProjectOutlined />,
        color: colors.primary[600],
        bgColor: colors.primary[50],
    },
    {
        key: 'isBlockers',
        label: 'Blockers',
        description: 'Track and resolve project impediments',
        icon: <StopOutlined />,
        color: colors.warning?.[500] || '#f59e0b',
        bgColor: colors.warning?.[50] || '#fffbeb',
    },
    {
        key: 'isCollaboration',
        label: 'Collaboration',
        description: 'Team discussions and collaborative workspaces',
        icon: <TeamOutlined />,
        color: colors.info?.[500] || '#3b82f6',
        bgColor: colors.info?.[50] || '#eff6ff',
    },
];

const CLIENT_SWITCH_CONFIG = [
    {
        key: 'isBacklogs',
        label: 'Backlogs',
        description: 'Manage product and sprint backlogs',
        icon: <UnorderedListOutlined />,
        color: colors.success?.[500] || '#10b981',
        bgColor: colors.success?.[50] || '#ecfdf5',
    },
    {
        key: 'isChangeRequest',
        label: 'Change Requests',
        description: 'Formal scope and requirement change tracking',
        icon: <SwapOutlined />,
        color: '#8b5cf6',
        bgColor: '#f5f3ff',
    },
    {
        key: 'isReviews',
        label: 'Reviews',
        description: 'Code reviews and approval workflows',
        icon: <StarOutlined />,
        color: '#ec4899',
        bgColor: '#fdf2f8',
    },
];

const TemplateIllustration = ({ type, color = colors.primary[400] }) => {
    const bg = rgbaFromHex(color, 0.1);
    if (type === 'standard') {
        return (
            <svg viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="80" height="64" fill={bg}/>
                <rect x="8" y="8" width="64" height="6" rx="2" fill={color} fillOpacity="0.3"/>
                <rect x="8" y="22" width="20" height="34" rx="3" fill={color} fillOpacity="0.2"/>
                <rect x="34" y="22" width="38" height="14" rx="3" fill={color} fillOpacity="0.1"/>
                <rect x="34" y="42" width="38" height="14" rx="3" fill={color} fillOpacity="0.1"/>
            </svg>
        );
    }
    if (type === 'agile') {
        return (
            <svg viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="80" height="64" fill={bg}/>
                <rect x="10" y="10" width="16" height="44" rx="2" fill={color} fillOpacity="0.2"/>
                <rect x="32" y="10" width="16" height="44" rx="2" fill={color} fillOpacity="0.2"/>
                <rect x="54" y="10" width="16" height="44" rx="2" fill={color} fillOpacity="0.2"/>
                <rect x="13" y="14" width="10" height="6" rx="1" fill={color} fillOpacity="0.4"/>
                <rect x="35" y="24" width="10" height="6" rx="1" fill={color} fillOpacity="0.4"/>
                <rect x="57" y="18" width="10" height="6" rx="1" fill={color} fillOpacity="0.4"/>
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="80" height="64" fill={bg}/><path d="M12 48L24 32L36 40L52 20L68 36" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/><circle cx="52" cy="20" r="4" fill={color}/>
        </svg>
    );
};

export default function CreateProjectModal({
    open,
    onCancel,
    onSuccess,
    canCreate = true,
    projectRequestId,
    initialValues,
}) {
    const {
        step,
        handleNext,
        handleBack,
        handleReset,
        templates,
        templatesLoading,
        selectedTemplate,
        handleTemplateSelect,
        form,
        categoryOptions,
        categoriesLoading,
        clientTab,
        setClientTab,
        PROJECT_CREATE_CLIENT_TAB,
        clientOptions,
        clientsLoading,
        setClientSearchText,
        setClientDropdownOpened,
        handleSubmit,
        isPending,
        handleCancel,
        PROJECT_TYPE_OPTIONS,
        PRIORITY_OPTIONS,
        moduleToggles,
        handleModuleToggle,
        isManagedClient,
    } = useCreateProjectModal({ onSuccess, onCancel, projectRequestId });

    const activePanelRef = React.useRef(null);
    const projectType = Form.useWatch('projectType', form);

    React.useEffect(() => { if (open && initialValues) form.setFieldsValue(initialValues); }, [open, initialValues, form]);

    React.useEffect(() => {
        if (open && step === 0) {
            const timer = setTimeout(() => {
                activePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [selectedTemplate, step, open]);

    const renderStepContent = () => {
        switch (step) {
            case 0: {
                // Determine which modules are required (cannot be turned off) for the selected template
                const requiredModulesMap = getRequiredModulesForTemplate(selectedTemplate, isManagedClient, projectType);
                
                const enabledCount = MODULE_SWITCH_CONFIG.filter(mod => {
                    const config = requiredModulesMap[mod.key];
                    return config?.allowed && moduleToggles[mod.key];
                }).length;
                const totalCount = MODULE_SWITCH_CONFIG.length;

                const getDisabledTooltip = (key) => {
                    if (key === 'isQA') return 'QA Testing is locked because this template enforces formal client review/submissions.';
                    if (key === 'isPMO') return 'PMO Oversight is locked because this template enforces project milestone submissions.';
                    return 'This module is required by the active template structure.';
                };

                const getUnavailableTooltip = (key) => {
                    if (!selectedTemplate) return '';
                    const isAugment = projectType === 'hourly_resource_augment' || projectType === 'fixed_recurring_resource_augment';
                    const isInHouse = projectType === 'in_house_internal';
                    const hasDeliveryWork = !!(selectedTemplate.usePhases || selectedTemplate.useMilestones || selectedTemplate.useSprints || selectedTemplate.useTasks);
                    const hasSubmission = selectedTemplate.submissionMode !== 'none';
                    const isMemoSubmissionPath = selectedTemplate.submissionMode === 'memo' || !!selectedTemplate.useSubmissionMemo;

                    switch (key) {
                        case 'isQA':
                            if (!selectedTemplate.useTasks) return 'QA Testing requires a template that supports Tasks.';
                            if (!hasSubmission) return 'QA Testing requires a template with submission mode enabled.';
                            if (isMemoSubmissionPath) return 'QA Testing is not available for Submission Memo templates.';
                            if (selectedTemplate.submissionMode !== 'formal' && selectedTemplate.submissionMode !== 'internal') return 'QA Testing requires formal or internal submission mode.';
                            return 'QA Testing is unavailable under the current configuration.';
                        case 'isPMO':
                            if (!hasDeliveryWork) return 'PMO Oversight requires a template with delivery structure.';
                            if (isAugment) return 'PMO Oversight is not available for resource augmentation projects.';
                            return 'PMO Oversight is unavailable.';
                        case 'isBlockers':
                            if (!hasDeliveryWork) return 'Blockers tracking requires a template with delivery structure.';
                            if (isAugment) return 'Blockers tracking is not available for resource augmentation projects.';
                            return 'Blockers tracking is unavailable.';
                        case 'isCollaboration':
                            if (!hasDeliveryWork) return 'Collaboration requires a template with delivery structure.';
                            if (isAugment) return 'Collaboration is not available for resource augmentation projects.';
                            return 'Collaboration is unavailable.';
                        case 'isBacklogs':
                            if (!selectedTemplate.useTasks) return 'Backlogs require a template that supports Tasks.';
                            if (!isManagedClient) return 'Backlogs require a Managed Platform Client (Client must be registered on Sprinto).';
                            return 'Backlogs are unavailable.';
                        case 'isChangeRequest':
                            if (isAugment) return 'Change Requests are not available for resource augmentation projects.';
                            if (isInHouse) return 'Change Requests are not available for in-house internal projects.';
                            if (selectedTemplate.submissionMode !== 'formal' && selectedTemplate.submissionMode !== 'memo') return 'Change Requests require formal or memo submission mode.';
                            if (!isManagedClient) return 'Change Requests require a Managed Platform Client (Client must be registered on Sprinto).';
                            return 'Change Requests are unavailable.';
                        case 'isReviews':
                            if (!hasDeliveryWork) return 'Reviews require a template with delivery structure.';
                            if (isAugment) return 'Reviews are not available for resource augmentation projects.';
                            if (!isManagedClient) return 'Reviews require a Managed Platform Client (Client must be registered on Sprinto).';
                            return 'Reviews are unavailable.';
                        default:
                            return '';
                    }
                };

                return (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <RolesSectionSubtitle style={{ marginBottom: 20 }}>Select a template to start organizing your content efficiently.</RolesSectionSubtitle>
                    {templatesLoading ? <Container $display="flex" $alignItems="center" $justifyContent="center" $padding="60px"><Spin size="large" /></Container> : (
                        <TemplateList style={{ flex: 1, minHeight: 0 }}>
                            {templates.map(t => {
                                const isSelected = (selectedTemplate?.id || selectedTemplate?._id) === (t.id || t._id);
                                return (
                                    <TemplateCard key={t.id || t._id} $selected={isSelected} onClick={() => handleTemplateSelect(t)}>
                                        <div className="card-header-row">
                                            <div className="illustration-box"><TemplateIllustration type={t.useSprints ? 'agile' : 'milestone'} color={t.useSprints ? colors.primary[400] : colors.success[500]} /></div>
                                            <div className="content-box"><div className="name">{t.name}</div><div className="description">{t.description || 'Professional workflow template.'}</div></div>
                                            <div className="meta-box"><div style={{ display: 'flex', gap: '4px' }}>{t.usePhases && <Tag bordered={false} color="blue">Phases</Tag>}{t.useSprints && <Tag bordered={false} color="purple">Sprints</Tag>}</div><CheckCircleFilled className="check-icon" /></div>
                                        </div>
                                        {isSelected && (
                                            <ModuleTogglesPanel ref={activePanelRef} onClick={(e) => e.stopPropagation()}>
                                                <ModuleTogglesPanelHeader>
                                                    <span className="panel-icon"><SettingOutlined /></span>
                                                    <span className="panel-title">Project Modules</span>
                                                    <span className="panel-subtitle">{enabledCount}/{totalCount} enabled</span>
                                                </ModuleTogglesPanelHeader>
                                                <ModuleTogglesGrid>
                                                    {MODULE_SWITCH_CONFIG.map(mod => {
                                                        const config = requiredModulesMap[mod.key] || { allowed: false, locked: true, value: false };
                                                        const isAllowed = !!config.allowed;
                                                        const isRequired = !!config.locked;
                                                        const isDisabled = !isAllowed || isRequired;
                                                        const isOn = isAllowed ? moduleToggles[mod.key] : false;

                                                        let tooltipTitle = undefined;
                                                        if (!isAllowed) {
                                                            tooltipTitle = getUnavailableTooltip(mod.key);
                                                        } else if (isRequired) {
                                                            tooltipTitle = getDisabledTooltip(mod.key);
                                                        }

                                                        return (
                                                            <Tooltip key={mod.key} title={tooltipTitle} placement="top">
                                                                <ModuleToggleRow $disabled={isDisabled} style={!isAllowed ? { opacity: 0.6 } : undefined}>
                                                                    <div className="toggle-icon" style={{ background: isOn ? mod.bgColor : colors.neutral[50], color: isOn ? mod.color : colors.neutral[300] }}>
                                                                        {mod.icon}
                                                                    </div>
                                                                    <div className="toggle-content">
                                                                        <div className="toggle-label">
                                                                            {mod.label}
                                                                            {!isAllowed && <span style={{ fontSize: '10px', color: colors.neutral[400], marginLeft: '6px', fontWeight: 'normal' }}>(Unavailable)</span>}
                                                                            {isAllowed && isRequired && <LockOutlined style={{ fontSize: 11, color: colors.neutral[400] }} />}
                                                                        </div>
                                                                        <div className="toggle-desc">{mod.description}</div>
                                                                    </div>
                                                                    <div className="toggle-switch">
                                                                        <Switch
                                                                            size="small"
                                                                            checked={isOn}
                                                                            disabled={isDisabled}
                                                                            onChange={(val) => handleModuleToggle(mod.key, val)}
                                                                        />
                                                                    </div>
                                                                </ModuleToggleRow>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </ModuleTogglesGrid>
                                                {(t.submissionMode === 'memo' || !!t.useSubmissionMemo) && (
                                                    <div style={{ padding: '10px 12px', background: colors.warning?.[50] || '#fffbeb', border: `1px solid ${colors.warning?.[100] || '#fef3c7'}`, borderRadius: '6px', fontSize: '11px', color: colors.warning?.[700] || '#b45309', marginTop: '10px', lineHeight: 1.4 }}>
                                                        The Submission Memo template is designed for submission workflows without QA Testing or PMO modules. If QA or PMO features are required, please select a different project template.
                                                    </div>
                                                )}
                                            </ModuleTogglesPanel>
                                        )}
                                    </TemplateCard>
                                );
                            })}
                        </TemplateList>
                    )}
                </div>
            );}
            case 1: return (
                <Form form={form} layout="vertical" style={{ width: '100%' }}>
                    <Form.Item name="name" label="Project name" rules={[{ required: true, message: 'Enter project name' }]}><Input placeholder="e.g. Website Redesign" size="large" /></Form.Item>
                    <FormRow>
                        <Form.Item name="projectType" label="Project Type" rules={[{ required: true }]} style={{ flex: 1 }}><Select placeholder="Select type" size="large" options={PROJECT_TYPE_OPTIONS} /></Form.Item>
                        <Form.Item name="categoryId" label="Category" style={{ flex: 1 }}><Select placeholder="Select category" size="large" allowClear options={categoryOptions} loading={categoriesLoading} /></Form.Item>
                    </FormRow>
                    <FormRow>
                        <Form.Item name="budget" label="Budget" style={{ flex: 1 }}><InputNumber placeholder="e.g. 50000" size="large" style={{ width: '100%' }} formatter={v => v == null ? '' : Number(v).toLocaleString()} parser={v => v == null ? '' : Number(String(v).replace(/\D/g, ''))} /></Form.Item>
                        <Form.Item name="priority" label="Priority" rules={[{ required: true }]} style={{ flex: 1 }}><Select placeholder="Select priority" size="large" options={PRIORITY_OPTIONS} /></Form.Item>
                    </FormRow>
                </Form>
            );
            case 2: return (
                <Form form={form} layout="vertical" style={{ width: '100%' }}>
                    <SliderTabStripScroll><SliderTab items={[{ key: PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO, label: 'My Clients' }, { key: PROJECT_CREATE_CLIENT_TAB.SPRINTO_EMAIL, label: 'Sprinto Client' }, { key: PROJECT_CREATE_CLIENT_TAB.OUTSIDE, label: 'Outside Sprinto Client' }, { key: PROJECT_CREATE_CLIENT_TAB.IN_HOUSE, label: 'In House Project' }]} activeKey={clientTab} onChange={setClientTab} /></SliderTabStripScroll>
                    <Container $display="flex" $flexDirection="column" $gap="12px" $marginTop="20px">
                        {clientTab === PROJECT_CREATE_CLIENT_TAB.MY_SPRINTO && <Form.Item name="clientId" label="Client" rules={[{ required: true }]}><Select placeholder="Select client" size="large" showSearch onSearch={setClientSearchText} options={clientOptions} loading={clientsLoading} onOpenChange={setClientDropdownOpened} /></Form.Item>}
                        {clientTab === PROJECT_CREATE_CLIENT_TAB.SPRINTO_EMAIL && <Form.Item name="sprintoClientEmail" label="Client email" rules={[{ required: true }, { type: 'email' }]}><Input placeholder="client@example.com" size="large" /></Form.Item>}
                        {clientTab === PROJECT_CREATE_CLIENT_TAB.OUTSIDE && <><Form.Item name="clientName" label="Client name" rules={[{ required: true }]}><Input placeholder="John Doe" size="large" /></Form.Item><Form.Item name="clientEmail" label="Client email" rules={[{ required: true }, { type: 'email' }]}><Input placeholder="john@example.com" size="large" /></Form.Item><Form.Item name="clientPhone" label="Client phone" rules={[{ required: true }]}><Input placeholder="+1..." size="large" /></Form.Item></>}
                        {clientTab === PROJECT_CREATE_CLIENT_TAB.IN_HOUSE && <RolesSectionSubtitle>The workspace is the client for this project.</RolesSectionSubtitle>}
                    </Container>

                    {(() => {
                        const requiredMap = getRequiredModulesForTemplate(selectedTemplate, isManagedClient, projectType);
                        
                        const enabledClientCount = CLIENT_SWITCH_CONFIG.filter(mod => {
                            const config = requiredMap[mod.key];
                            return config?.allowed && moduleToggles[mod.key];
                        }).length;
                        const totalClientCount = CLIENT_SWITCH_CONFIG.length;

                        const getDisabledTooltip = (key) => {
                            return 'This module is required by the active template structure.';
                        };

                        const getUnavailableTooltip = (key) => {
                            if (!selectedTemplate) return '';
                            const isAugment = projectType === 'hourly_resource_augment' || projectType === 'fixed_recurring_resource_augment';
                            const isInHouse = projectType === 'in_house_internal';
                            const hasDeliveryWork = !!(selectedTemplate.usePhases || selectedTemplate.useMilestones || selectedTemplate.useSprints || selectedTemplate.useTasks);

                            switch (key) {
                                case 'isBacklogs':
                                    if (!selectedTemplate.useTasks) return 'Backlogs require a template that supports Tasks.';
                                    if (!isManagedClient) return 'Backlogs require a Managed Platform Client (Client must be registered on Sprinto).';
                                    return 'Backlogs are unavailable.';
                                case 'isChangeRequest':
                                    if (isAugment) return 'Change Requests are not available for resource augmentation projects.';
                                    if (isInHouse) return 'Change Requests are not available for in-house internal projects.';
                                    if (selectedTemplate.submissionMode !== 'formal' && selectedTemplate.submissionMode !== 'memo') return 'Change Requests require formal or memo submission mode.';
                                    if (!isManagedClient) return 'Change Requests require a Managed Platform Client (Client must be registered on Sprinto).';
                                    return 'Change Requests are unavailable.';
                                case 'isReviews':
                                    if (!hasDeliveryWork) return 'Reviews require a template with delivery structure.';
                                    if (isAugment) return 'Reviews are not available for resource augmentation projects.';
                                    if (!isManagedClient) return 'Reviews require a Managed Platform Client (Client must be registered on Sprinto).';
                                    return 'Reviews are unavailable.';
                                default:
                                    return '';
                            }
                        };

                        return (
                            <ModuleTogglesPanel style={{ marginTop: 24 }}>
                                <ModuleTogglesPanelHeader>
                                    <span className="panel-icon"><SettingOutlined /></span>
                                    <span className="panel-title">Client-scoped Modules</span>
                                    <span className="panel-subtitle">
                                        {enabledClientCount}/{totalClientCount} enabled
                                    </span>
                                </ModuleTogglesPanelHeader>
                                <ModuleTogglesGrid>
                                    {CLIENT_SWITCH_CONFIG.map(mod => {
                                        const config = requiredMap[mod.key] || { allowed: false, locked: true, value: false };
                                        const isAllowed = !!config.allowed;
                                        const isRequired = !!config.locked;
                                        const isDisabled = !isAllowed || isRequired;
                                        const isOn = isAllowed ? moduleToggles[mod.key] : false;

                                        let tooltipTitle = undefined;
                                        if (!isAllowed) {
                                            tooltipTitle = getUnavailableTooltip(mod.key);
                                        } else if (isRequired) {
                                            tooltipTitle = getDisabledTooltip(mod.key);
                                        }

                                        return (
                                            <Tooltip key={mod.key} title={tooltipTitle} placement="top">
                                                <ModuleToggleRow $disabled={isDisabled} style={!isAllowed ? { opacity: 0.6 } : undefined}>
                                                    <div className="toggle-icon" style={{ background: isOn ? mod.bgColor : colors.neutral[50], color: isOn ? mod.color : colors.neutral[300] }}>
                                                        {mod.icon}
                                                    </div>
                                                    <div className="toggle-content">
                                                        <div className="toggle-label">
                                                            {mod.label}
                                                            {!isAllowed && <span style={{ fontSize: '10px', color: colors.neutral[400], marginLeft: '6px', fontWeight: 'normal' }}>(Unavailable)</span>}
                                                            {isAllowed && isRequired && <LockOutlined style={{ fontSize: 11, color: colors.neutral[400] }} />}
                                                        </div>
                                                        <div className="toggle-desc">{mod.description}</div>
                                                    </div>
                                                    <div className="toggle-switch">
                                                        <Switch
                                                            size="small"
                                                            checked={isOn}
                                                            disabled={isDisabled}
                                                            onChange={(val) => handleModuleToggle(mod.key, val)}
                                                        />
                                                    </div>
                                                </ModuleToggleRow>
                                            </Tooltip>
                                        );
                                    })}
                                </ModuleTogglesGrid>
                            </ModuleTogglesPanel>
                        );
                    })()}
                </Form>
            );
            default: return null;
        }
    };

    return (
        <AppModal
            size="lg"
            lgWidth="min(860px, calc(100vw - 48px))"
            title="Create Project"
            open={open}
            onCancel={handleCancel}
            footer={null}
            styles={{ body: { paddingTop: 24, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
        >
            <StyledSteps
                current={step}
                responsive={false}
                items={[
                    { title: 'Template', icon: <AppstoreOutlined /> },
                    { title: 'Details', icon: <EditOutlined /> },
                    { title: 'Client', icon: <UserOutlined /> },
                ]}
            />
            <CreateRoleModalBody style={{ overflowY: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>{renderStepContent()}</CreateRoleModalBody>
            <ResponsiveFooter>
                <Button $primary={false} onClick={handleReset} icon={<ReloadOutlined />}>Reset</Button>
                <div style={{ display: 'flex', gap: '12px' }}>
                    {step > 0 && <Button $primary={false} onClick={handleBack} icon={<LeftOutlined />}>Back</Button>}
                    {step < 2 ? (
                        <Button $primary $blackOnLight onClick={handleNext}>Next <RightOutlined /></Button>
                    ) : (
                        canCreate && <Button $primary $blackOnLight loading={isPending} onClick={handleSubmit}>Create Project</Button>
                    )}
                </div>
            </ResponsiveFooter>
        </AppModal>
    );
}
